<template>
  <li v-if="kindVisible">
    <div class="sticky top-0 z-10 bg-white">
      <button
        class="search-toggle"
        :aria-pressed="filterVisible ? 'true' : 'false'"
        @click="toggleFilter"
      >
        {{ t(kind) }}
        <svg
          class="ml-auto h-4 w-4"
          focusable="false"
          aria-hidden="true"
          width="16"
          height="16"
        >
          <use v-bind="{ 'xlink:href': '#arrow-down' }" />
        </svg>
      </button>

      <button
        v-if="filterVisible && filterIsSet"
        class="mb-10px flex w-full items-center py-4 text-left leading-none"
        @click="resetFilter"
      >
        <svg
          class="mr-5 h-3 w-3"
          focusable="false"
          aria-hidden="true"
          width="12"
          height="12"
        >
          <use v-bind="{ 'xlink:href': '#arrow-down' }" />
        </svg>
        <span>{{ t('all') }}</span>
      </button>

      <!-- Search -->
      <div
        class="flex items-center pb-10px"
        v-if="filterVisible && kind !== 'COLOR'"
      >
        <label :for="`filter-${kind}`" class="mr-4 text-ccc">
          <span class="sr-only">{{ t('search') }}</span>
          <svg
            style="width: 1em; height: 1em"
            focusable="false"
            aria-hidden="true"
            width="16"
            height="16"
          >
            <use xlink:href="#magnifier" />
          </svg>
        </label>
        <input
          :id="`filter-${kind}`"
          type="search"
          :placeholder="t('search')"
          class="w-full text-root"
          autocomplete="off"
          @input="handleFilterInput"
          v-model="filterTerm"
        />
        <button
          @click="resetFilterInput"
          class="px-1 py-1"
          v-if="filterTerm"
          type="reset"
        >
          <span class="sr-only">{{ t('reset') }}</span>
          <svg
            focusable="false"
            aria-hidden="true"
            class="h-3 w-3"
            width="12"
            height="12"
          >
            <use xlink:href="#close" />
          </svg>
        </button>
      </div>
    </div>

    <div
      :class="{ 'search-vocabulary-wrapper': kind !== 'COLOR' }"
      v-if="filterVisible"
    >
      <div v-if="kind !== 'COLOR' && loading" class="py-4 text-center text-ccc">
        {{ t('loading') }}…
      </div>
      <ul class="divide-y relative divide-ccc pb-15" v-if="kind !== 'COLOR'">
        <filter-vocabulary-component
          v-for="vocabulary in vocabularies"
          :key="`${kind}-${vocabulary.id}`"
          :kind="kind"
          :vocabulary="vocabulary"
          :expanded-items="expandedItems"
          @expandChildren="fetchChildren"
        />
      </ul>
      <ul v-if="kind === 'COLOR'" class="mb-8">
        <li v-for="color in allColors" :key="color">
          <filter-color-component
            :color="color"
            :disabled="!colors.includes(color)"
          >
          </filter-color-component>
        </li>
      </ul>
    </div>
  </li>
</template>

<i18n>
{
  "en": {
    "search": "Search",
    "reset": "Reset",
    "all": "All",
    "loading": "Loading",
    "load_more": "Load more",
    "PERSON": "Artists/Producer",
    "THEME": "Theme",
    "DISTRICT": "District",
    "CLASSIFICATION": "Classification",
    "CLOCK_DRIVE": "Power Source",
    "ICONCLASS": "Subject",
    "MATERIAL": "Material",
    "TECHNIQUE": "Technique",
    "PLACE": "Place",
    "COIN_MINT": "Mint",
    "COIN_TERRITORY": "Mint Territory",
    "COLOR": "Color"
  },
  "de": {
    "search": "Suchen",
    "reset": "Zurücksetzen",
    "all": "Alle",
    "loading": "Lädt",
    "load_more": "Mehr laden",
    "PERSON": "Künstler:in/Hersteller:in",
    "THEME": "Thema",
    "DISTRICT": "Bezirk",
    "CLASSIFICATION": "Objektart",
    "CLOCK_DRIVE": "Antriebsart",
    "ICONCLASS": "Bildmotiv",
    "MATERIAL": "Material",
    "TECHNIQUE": "Technik",
    "PLACE": "Ort",
    "COIN_MINT": "Münzstätte",
    "COIN_TERRITORY": "Münzstand",
    "COLOR": "Farbe"
  }
}
</i18n>
<script lang="ts">
import gql from 'graphql-tag';
import {
  PropType,
  Ref,
  SetupContext,
  computed,
  defineComponent,
  ref,
  inject,
  watch,
} from 'vue';
import { AvailableFilters, VocabularyFilter } from '../@types/filter';
import { FilterResultEntry, Vocabulary } from '../@types/vocabulary';
import {
  availableFilters,
  getFilterProperty,
  sanitizedFilter,
  vocabularyFilter,
} from '../utils/filter';
import { createApi, executeGqlQuery } from '../utils/gql';
import { lang } from '../utils/utils';
import FilterVocabularyComponent from './FilterVocabularyComponent.vue';
import FilterColorComponent from './FilterColorComponent.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'FilterKindComponent',

  components: {
    FilterVocabularyComponent,
    FilterColorComponent,
  },

  props: {
    kind: {
      type: String,
      required: true,
    },
    filterVisible: {
      type: Boolean,
      default: false,
    },
    expandedItems: {
      type: Array as PropType<string[]>,
      required: false,
    },
  },

  setup(props, context: SetupContext) {
    const supportsIntersectionObserver = 'IntersectionObserver' in window;
    const loading = ref(false);
    const allLoaded = ref(false);
    const bottom: Ref<HTMLElement | null> = ref(null);
    const Fuse: Ref<any> = ref(null);

    const allColors = inject('allColors');
    const filterTerm = ref('');

    const colorsQuery = computed(() => {
      return gql`query objects(
        $filter: Filter
        $lang: Lang
      ) {
        objects(filter: $filter, lang: $lang) {
          filter {
            ${getFilterProperty(props.kind)}
          }
        }
      }`;
    });

    const vocabulariesQuery = computed(() => {
      return gql`query objects(
        $filter: Filter
        $lang: Lang
        $parent: String
      ) {
        objects(filter: $filter, lang: $lang) {
          filter {
            ${getFilterProperty(props.kind)}(parent: $parent) {
              id
              text
              count
              parent
              leaf
            }
          }
        }
      }`;
    });

    const filterProperty = getFilterProperty(props.kind);

    const kindVisible = computed(() => {
      return (
        availableFilters[filterProperty as keyof AvailableFilters] &&
        (
          availableFilters[
            filterProperty as keyof AvailableFilters
          ] as FilterResultEntry[]
        ).length !== 0
      );
    });

    const toggleFilter = () => {
      context.emit('toggleFilter', props.kind);
    };

    const allVocabularies: Ref<Vocabulary[]> = ref([]);
    const vocabularies: Ref<Vocabulary[]> = ref([]);
    const colors: Ref<string[]> = ref([]);
    const isNested = computed(() => {
      return vocabularies.value.some((vocabulary) => !vocabulary.leaf);
    });

    const api = createApi();

    const fetchVocabularies = async (): Promise<number> => {
      loading.value = true;
      const length = await executeGqlQuery(
        api,
        vocabulariesQuery.value,
        {
          filter: sanitizedFilter.value,
          lang: lang.toUpperCase(),
          parent: isNested.value && filterTerm.value ? undefined : null,
        },
        (data: any): Vocabulary[] => {
          return data.objects.filter[
            getFilterProperty(props.kind) as keyof AvailableFilters
          ];
        }
      ).then((result: Vocabulary[]) => {
        loading.value = false;

        if (!result) {
          return 0;
        }
        vocabularies.value = result;

        return result.length;
      });

      return length;
    };

    const fetchColors = async (): Promise<number> => {
      loading.value = true;
      const length = await executeGqlQuery(
        api,
        colorsQuery.value,
        {
          filter: sanitizedFilter.value,
          lang: lang.toUpperCase(),
        },
        (data: any): string[] => {
          return data.objects.filter[
            getFilterProperty(props.kind) as keyof AvailableFilters
          ];
        }
      ).then((result: string[]) => {
        loading.value = false;

        if (!result) {
          return 0;
        }
        colors.value = result;

        return result.length;
      });

      return length;
    };

    watch(
      () => props.filterVisible,
      (visible) => {
        if (visible) {
          if (props.kind === 'COLOR') {
            fetchColors();
          } else {
            fetchVocabularies();
          }
        }
      }
    );

    const appendChildrenRecursive = (
      array: Vocabulary[],
      id: string,
      children: Vocabulary[]
    ): (Vocabulary & FilterResultEntry)[] => {
      return array.map((vocabulary) => {
        if (vocabulary.id === id) {
          return { ...vocabulary, children } as Vocabulary & FilterResultEntry;
        }
        if (vocabulary.children && vocabulary.children.length) {
          vocabulary.children = appendChildrenRecursive(
            vocabulary.children,
            id,
            children
          );
        }
        return vocabulary as Vocabulary & FilterResultEntry;
      });
    };

    const fetchChildren = (
      vocabularyId: string,
      stayExpanded = false
    ): void => {
      if (!stayExpanded) {
        context.emit('toggleExpandedItem', {
          kind: props.kind,
          item: vocabularyId,
        });
      }
      executeGqlQuery(
        api,
        vocabulariesQuery.value,
        {
          filter: sanitizedFilter.value,
          lang: lang.toUpperCase(),
          parent: vocabularyId,
        },
        (data: any): Vocabulary[] => {
          return data.objects.filter[
            getFilterProperty(props.kind) as keyof AvailableFilters
          ];
        }
      ).then((result: Vocabulary[]) => {
        vocabularies.value = appendChildrenRecursive(
          vocabularies.value,
          vocabularyId,
          result
        );
      });
    };

    // Fetch vocabularies for visible filters if filter was updated.
    watch(
      () => sanitizedFilter.value,
      () => {
        if (props.filterVisible) {
          if (props.kind === 'COLOR') {
            fetchColors();
          } else {
            fetchVocabularies().then(async () => {
              for (const item of props.expandedItems) {
                fetchChildren(item, true);
              }
            });
          }
        }
      }
    );

    const filterVocabularies = () => {
      if (Fuse.value && filterTerm.value) {
        const fuse = new Fuse.value(vocabularies.value, { keys: ['text'] });
        vocabularies.value = fuse
          .search(filterTerm.value)
          .map((result: any) => result.item);
      }
    };

    let timeout: any;
    const handleFilterInput = async () => {
      const { default: FuseClass } = await import(
        /* webpackChunkName: "fuse" */ 'fuse.js'
      );
      Fuse.value = FuseClass;
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        fetchVocabularies().then(filterVocabularies);
      }, 700);
    };

    const resetFilterInput = () => {
      filterTerm.value = '';
      handleFilterInput();
    };

    const filterIsSet = computed(() => {
      if (vocabularyFilter[filterProperty as keyof VocabularyFilter]) {
        return true;
      }
      return false;
    });

    const resetFilter = () => {
      vocabularyFilter[filterProperty as keyof VocabularyFilter] = undefined;
    };

    return {
      kindVisible,
      toggleFilter,
      filterTerm,
      isNested,
      filterIsSet,
      resetFilter,
      vocabularies,
      allColors,
      colors,
      loading,
      fetchChildren,
      allLoaded,
      bottom,
      handleFilterInput,
      resetFilterInput,
      supportsIntersectionObserver,
      ...useI18n(),
    };
  },
});
</script>
